import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Typography, LinearProgress, Box, Paper, Divider,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { Add, ExpandMore } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EpcClientSelect } from '../../components/EpcClientSelect';
import { PageHeader } from '../../components/PageHeader';
import { epcApi, type EpcMilestone } from '../../api/epc.api';
import { epcSiteMonitoringApi, type ClientRollupProject } from '../../api/epcSiteMonitoring.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const fmtDate = (s?: string | null) => (s ? String(s).slice(0, 10) : '—');
const statusColor: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  pending: 'default', in_progress: 'info', completed: 'success', on_hold: 'warning', cancelled: 'error',
};

export function EpcMilestonesPage() {
  const { hasPermission } = useAuth();
  const [clientId, setClientId] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const cid = clientId ? Number(clientId) : undefined;

  const { data: rollup, isLoading } = useQuery({ queryKey: ['epc-client-rollup', cid], queryFn: () => epcSiteMonitoringApi.clientRollup(cid as number), enabled: Boolean(cid) });

  return (
    <>
      <PageHeader title="EPC Milestones" subtitle="Client → project → site execution. Cumulative milestones per project; expand a project for its site-wise progress." />

      <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap" useFlexGap>
        <EpcClientSelect value={clientId} onChange={setClientId} helperText={cid ? undefined : 'Select a client'} />
        {rollup && <Chip color="primary" label={`${rollup.projectCount} project${rollup.projectCount === 1 ? '' : 's'} · ${rollup.siteCount} site${rollup.siteCount === 1 ? '' : 's'}`} />}
      </Stack>

      {!cid && <Alert severity="info" sx={{ mb: 2 }}>Select a client to view its projects and cumulative milestones.</Alert>}
      {cid && !isLoading && rollup && rollup.projects.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No monitored sites for this client yet.</Alert>}

      {(rollup?.projects ?? []).map((p) => (
        <ProjectAccordion key={p.projectId ?? 'none'} project={p}
          canCreate={hasPermission('epc_milestone.create')} canUpdate={hasPermission('epc_milestone.update')} onToast={setToast} />
      ))}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function ProjectAccordion({ project, canCreate, canUpdate, onToast }: { project: ClientRollupProject; canCreate: boolean; canUpdate: boolean; onToast: (m: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion expanded={expanded} onChange={(_e, v) => setExpanded(v)} sx={{ mb: 1 }} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', pr: 2 }} flexWrap="wrap" useFlexGap>
          <Typography sx={{ minWidth: 240, fontWeight: 600 }}>{project.projectName ?? 'Unassigned project'}{project.projectCode ? ` (${project.projectCode})` : ''}</Typography>
          <Chip size="small" label={`${project.siteCount} site${project.siteCount === 1 ? '' : 's'}`} />
          <Box sx={{ flex: 1, minWidth: 160 }}><LinearProgress variant="determinate" value={project.cumulativePercent} sx={{ height: 10, borderRadius: 1 }} color={project.cumulativePercent === 100 ? 'success' : 'primary'} /></Box>
          <Typography variant="h6" sx={{ minWidth: 56, textAlign: 'right' }}>{project.cumulativePercent}%</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {/* Cumulative milestones = per-service execution across all the project's sites. */}
        <Typography variant="overline" color="text.secondary">Cumulative milestones</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
          {project.services.map((s) => <Chip key={s.serviceType} size="small" variant="outlined" color={s.percent === 100 ? 'success' : 'default'} label={`${s.serviceType}: ${s.percent}%`} />)}
        </Stack>

        <Divider sx={{ mb: 1 }} />
        <Typography variant="overline" color="text.secondary">Sites (site-wise)</Typography>
        <Stack spacing={0.75} mb={1.5}>
          {project.sites.map((s) => (
            <Stack key={s.monitoringId} direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" sx={{ minWidth: 220 }}><strong>{s.siteName ?? `#${s.siteId}`}</strong>{s.siteCode ? ` (${s.siteCode})` : ''}</Typography>
              <Box sx={{ flex: 1, minWidth: 140 }}><LinearProgress variant="determinate" value={s.overallPercent} sx={{ height: 8, borderRadius: 1 }} color={s.overallPercent === 100 ? 'success' : 'primary'} /></Box>
              <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>{s.overallPercent}%</Typography>
              <Chip size="small" variant="outlined" color={s.currentStage ? 'warning' : 'success'} label={s.currentStage ?? 'Done'} sx={{ minWidth: 150 }} />
            </Stack>
          ))}
        </Stack>

        {project.projectId != null && <ProjectMilestones projectId={project.projectId} canCreate={canCreate} canUpdate={canUpdate} onToast={onToast} />}
      </AccordionDetails>
    </Accordion>
  );
}

function ProjectMilestones({ projectId, canCreate, canUpdate, onToast }: { projectId: number; canCreate: boolean; canUpdate: boolean; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ['epc-milestones', projectId], queryFn: () => epcApi.listMilestones({ page: 1, limit: 100, projectId }) });
  const progressMut = useMutation({
    mutationFn: ({ id, progressPct }: { id: number; progressPct: number }) => epcApi.setProgress(id, { progressPct }),
    onSuccess: () => { onToast('Progress updated'); qc.invalidateQueries({ queryKey: ['epc-milestones', projectId] }); qc.invalidateQueries({ queryKey: ['epc-client-rollup'] }); },
    onError: (e) => onToast(extractApiError(e)),
  });
  const milestones = data?.data ?? [];
  return (
    <>
      <Divider sx={{ mb: 1 }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Typography variant="overline" color="text.secondary">Milestones</Typography>
        {canCreate && <Button size="small" startIcon={<Add />} onClick={() => setOpen(true)}>New Milestone</Button>}
      </Stack>
      {milestones.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No milestones yet.</Typography>
      ) : (
        <Stack spacing={0.5}>
          {milestones.map((m: EpcMilestone) => (
            <Paper key={m.id} variant="outlined" sx={{ p: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ minWidth: 200 }}><strong>#{m.sequence}</strong> {m.name}</Typography>
                <Chip size="small" color={statusColor[m.status]} label={human(m.status)} />
                <Box sx={{ flex: 1, minWidth: 120 }}><LinearProgress variant="determinate" value={m.progressPct} sx={{ height: 8, borderRadius: 1 }} /></Box>
                <Typography variant="caption" sx={{ minWidth: 40 }}>{m.progressPct}%</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>End: {fmtDate(m.plannedEndDate)}</Typography>
                {canUpdate && m.status !== 'completed' && m.status !== 'cancelled' && (
                  <Stack direction="row" spacing={0.5}>
                    {[25, 50, 75, 100].map((p) => <Button key={p} size="small" variant="outlined" onClick={() => progressMut.mutate({ id: m.id, progressPct: p })}>{p}%</Button>)}
                  </Stack>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
      {open && <NewMilestoneDialog projectId={projectId} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onToast('Milestone created'); qc.invalidateQueries({ queryKey: ['epc-milestones', projectId] }); }} />}
    </>
  );
}

function NewMilestoneDialog({ projectId, onClose, onDone }: { projectId: number; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ name: '', sequence: 1, weight: 1, plannedStartDate: '', plannedEndDate: '' });
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => epcApi.createMilestone({ projectId, name: form.name, sequence: form.sequence, weight: form.weight, plannedStartDate: form.plannedStartDate || undefined, plannedEndDate: form.plannedEndDate || undefined }),
    onSuccess: onDone, onError: (e) => setError(extractApiError(e)),
  });
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Milestone (Project #{projectId})</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Sequence" type="number" value={form.sequence} onChange={(e) => setForm({ ...form, sequence: Number(e.target.value) })} />
            <TextField label="Weight" type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: Number(e.target.value) })} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Planned Start" type="date" InputLabelProps={{ shrink: true }} value={form.plannedStartDate} onChange={(e) => setForm({ ...form, plannedStartDate: e.target.value })} fullWidth />
            <TextField label="Planned End" type="date" InputLabelProps={{ shrink: true }} value={form.plannedEndDate} onChange={(e) => setForm({ ...form, plannedEndDate: e.target.value })} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={form.name.length < 2 || mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
